import { ArrowLeft, CalendarPlus, CheckCircle2, ChevronDown, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminCreateMeetingResponse, CreateMeetingRequest } from '@hiredude/meet-shared';

import {
  errorMessage,
  isSessionExpired,
  REQUEST_TIMEOUT_MS,
  withTimeout,
} from '@/components/admin/adminError';
import { nextHalfHour, toOffsetIso } from '@/components/admin/datetime';
import {
  GUEST_LINK_DESCRIPTION,
  HOST_LINK_DESCRIPTION,
  JoinLinkRow,
} from '@/components/admin/joinLink';
import { JoinWindowSummary } from '@/components/admin/joinWindow';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { adminCreateMeeting } from '@/lib/adminApi';
import { cn } from '@/lib/utils';

/** Mirrors createMeetingSchema in the API. Kept in sync by hand, so both reject the same input. */
const LIMITS = {
  title: 160,
  name: 80,
  duration: { min: 5, max: 480 },
  participants: { min: 2, max: 50 },
  externalRef: 120,
  interviewId: 80,
} as const;

const DURATION_PRESETS = [15, 30, 45, 60, 90] as const;
const CUSTOM_DURATION = 'custom';

const RECORDING_OPTIONS = [
  { value: 'auto', label: 'Automatic', hint: 'Starts on its own as soon as the call does.' },
  { value: 'manual', label: 'Manual', hint: 'Waits until someone presses start in the console.' },
  { value: 'off', label: 'Off', hint: 'Never records. Nothing is written to storage.' },
] as const;

interface PartyFields {
  name: string;
  email: string;
  phone: string;
}

interface FormState {
  title: string;
  startsAt: string;
  durationChoice: string;
  customDuration: string;
  host: PartyFields;
  guest: PartyFields;
  recording: 'auto' | 'manual' | 'off';
  maxParticipants: string;
  companyRequestId: string;
  sortedCandidateId: string;
  interviewId: string;
}

const EMPTY_PARTY: PartyFields = { name: '', email: '', phone: '' };

function blankForm(): FormState {
  return {
    title: '',
    startsAt: nextHalfHour(),
    durationChoice: '45',
    customDuration: '',
    host: { ...EMPTY_PARTY },
    guest: { ...EMPTY_PARTY },
    recording: 'auto',
    maxParticipants: '',
    companyRequestId: '',
    sortedCandidateId: '',
    interviewId: '',
  };
}

type Errors = Partial<Record<string, string>>;

/** Loose on purpose: the API is the authority, this only catches obvious typos. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateParty(party: PartyFields, role: 'host' | 'guest', errors: Errors): void {
  const label = role === 'host' ? 'Interviewer' : 'Candidate';

  if (!party.name.trim()) errors[`${role}.name`] = `${label} name is required.`;
  else if (party.name.trim().length > LIMITS.name) {
    errors[`${role}.name`] = `Keep this under ${LIMITS.name} characters.`;
  }

  if (party.email.trim() && !EMAIL.test(party.email.trim())) {
    errors[`${role}.email`] = 'That does not look like an email address.';
  }

  const phone = party.phone.trim();
  if (phone && (phone.length < 5 || phone.length > 20)) {
    errors[`${role}.phone`] = 'A phone number must be 5 to 20 characters.';
  }
}

function validate(form: FormState): { errors: Errors; request?: CreateMeetingRequest } {
  const errors: Errors = {};

  const title = form.title.trim();
  if (!title) errors.title = 'A title is required.';
  else if (title.length > LIMITS.title) {
    errors.title = `Keep this under ${LIMITS.title} characters.`;
  }

  const startsAt = toOffsetIso(form.startsAt);
  if (!startsAt) errors.startsAt = 'Pick a date and time.';

  const durationRaw =
    form.durationChoice === CUSTOM_DURATION ? form.customDuration.trim() : form.durationChoice;
  const durationMin = Number(durationRaw);
  if (!durationRaw || !Number.isInteger(durationMin)) {
    errors.duration = 'Enter the length in whole minutes.';
  } else if (durationMin < LIMITS.duration.min || durationMin > LIMITS.duration.max) {
    errors.duration = `Between ${LIMITS.duration.min} and ${LIMITS.duration.max} minutes.`;
  }

  validateParty(form.host, 'host', errors);
  validateParty(form.guest, 'guest', errors);

  let maxParticipants: number | undefined;
  const maxRaw = form.maxParticipants.trim();
  if (maxRaw) {
    const parsed = Number(maxRaw);
    if (!Number.isInteger(parsed)) errors.maxParticipants = 'Enter a whole number.';
    else if (parsed < LIMITS.participants.min || parsed > LIMITS.participants.max) {
      errors.maxParticipants = `Between ${LIMITS.participants.min} and ${LIMITS.participants.max}.`;
    } else maxParticipants = parsed;
  }

  // This becomes the LiveKit room name, which is why it is far stricter than
  // the two reference fields beside it.
  const interviewId = form.interviewId.trim();
  if (interviewId && !/^[A-Za-z0-9_-]+$/.test(interviewId)) {
    errors.interviewId = 'Only letters, digits, hyphens and underscores — it becomes the room name.';
  } else if (interviewId.length > LIMITS.interviewId) {
    errors.interviewId = `Keep this under ${LIMITS.interviewId} characters.`;
  }

  for (const [field, value] of [
    ['companyRequestId', form.companyRequestId],
    ['sortedCandidateId', form.sortedCandidateId],
  ] as const) {
    if (value.trim().length > LIMITS.externalRef) {
      errors[field] = `Keep this under ${LIMITS.externalRef} characters.`;
    }
  }

  if (Object.keys(errors).length > 0 || !startsAt) return { errors };

  // Blank optional fields are omitted rather than sent empty: the API's schema
  // rejects an empty string where it would accept the field being absent.
  const party = (fields: PartyFields) => ({
    name: fields.name.trim(),
    ...(fields.email.trim() ? { email: fields.email.trim() } : {}),
    ...(fields.phone.trim() ? { phone: fields.phone.trim() } : {}),
  });

  const externalRef = {
    ...(form.companyRequestId.trim() ? { companyRequestId: form.companyRequestId.trim() } : {}),
    ...(form.sortedCandidateId.trim() ? { sortedCandidateId: form.sortedCandidateId.trim() } : {}),
    ...(interviewId ? { interviewId } : {}),
  };

  return {
    errors,
    request: {
      title,
      startsAt,
      durationMin,
      host: party(form.host),
      guest: party(form.guest),
      recording: form.recording,
      ...(maxParticipants !== undefined ? { maxParticipants } : {}),
      ...(Object.keys(externalRef).length > 0 ? { externalRef } : {}),
    },
  };
}

export function AdminNewMeetingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(blankForm);
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [created, setCreated] = useState<AdminCreateMeetingResponse | null>(null);
  const [showAts, setShowAts] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const setParty = (role: 'host' | 'guest', field: keyof PartyFields, value: string) =>
    setForm((current) => ({ ...current, [role]: { ...current[role], [field]: value } }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    const { errors: found, request } = validate(form);
    setErrors(found);
    if (!request) return;

    setPending(true);
    try {
      const meeting = await withTimeout(
        adminCreateMeeting(request),
        REQUEST_TIMEOUT_MS,
        'The server did not respond. The meeting may or may not have been created — check the list.',
      );
      setCreated(meeting);
      window.scrollTo({ top: 0 });
    } catch (error) {
      if (isSessionExpired(error)) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setSubmitError(errorMessage(error, 'Could not create the meeting. Try again.'));
    } finally {
      setPending(false);
    }
  };

  if (created) {
    return (
      <CreatedView
        meeting={created}
        onAnother={() => {
          setCreated(null);
          setForm(blankForm());
          setErrors({});
          setShowAts(false);
        }}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6 lg:p-8">
      <BackLink />

      <header className="mt-4">
        <h1 className="text-2xl font-semibold tracking-tight">New meeting</h1>
        <p className="text-muted-foreground mt-1 text-sm text-pretty">
          Creates the room and returns a join link for each side. Nothing is sent to anyone — you
          share the links yourself.
        </p>
      </header>

      <form onSubmit={submit} noValidate className="mt-6 space-y-4">
        <Card className="space-y-5 p-4 sm:p-6">
          <Field
            id="title"
            label="Title"
            error={errors.title}
            hint="What the interviewer and candidate will see."
          >
            <Input
              id="title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Senior Backend Engineer — first round"
              maxLength={LIMITS.title}
              aria-invalid={Boolean(errors.title)}
              autoFocus
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              id="startsAt"
              label="Starts at"
              error={errors.startsAt}
              hint="Your local time. The link works any time until the interview ends."
            >
              <Input
                id="startsAt"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => set('startsAt', e.target.value)}
                aria-invalid={Boolean(errors.startsAt)}
              />
            </Field>

            <Field id="duration" label="Length" error={errors.duration}>
              <Select
                id="duration"
                value={form.durationChoice}
                onChange={(e) => set('durationChoice', e.target.value)}
                aria-invalid={Boolean(errors.duration)}
              >
                {DURATION_PRESETS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {minutes} minutes
                  </option>
                ))}
                <option value={CUSTOM_DURATION}>Custom…</option>
              </Select>

              {form.durationChoice === CUSTOM_DURATION && (
                <Input
                  className="mt-2"
                  type="number"
                  inputMode="numeric"
                  min={LIMITS.duration.min}
                  max={LIMITS.duration.max}
                  value={form.customDuration}
                  onChange={(e) => set('customDuration', e.target.value)}
                  placeholder="Minutes"
                  aria-label="Custom length in minutes"
                  aria-invalid={Boolean(errors.duration)}
                />
              )}
            </Field>
          </div>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <PartyCard
            role="host"
            heading="Interviewer"
            caption="Gets host controls: mute, remove and end for everyone."
            fields={form.host}
            errors={errors}
            onChange={setParty}
          />
          <PartyCard
            role="guest"
            heading="Candidate"
            caption="Joins as a normal participant."
            fields={form.guest}
            errors={errors}
            onChange={setParty}
          />
        </div>

        <Card className="space-y-5 p-4 sm:p-6">
          <fieldset>
            <legend className="text-sm font-medium">Recording</legend>
            <div className="mt-3 space-y-2">
              {RECORDING_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                    form.recording === option.value
                      ? 'border-primary/50 bg-primary/10'
                      : 'border-border/60 hover:bg-white/[0.03]',
                  )}
                >
                  <input
                    type="radio"
                    name="recording"
                    value={option.value}
                    checked={form.recording === option.value}
                    onChange={() => set('recording', option.value)}
                    className="accent-primary mt-0.5 size-4 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="text-muted-foreground block text-xs text-pretty">
                      {option.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <Separator />

          <Field
            id="maxParticipants"
            label="Participant limit"
            optional
            error={errors.maxParticipants}
            hint="Leave blank for the server default."
          >
            <Input
              id="maxParticipants"
              type="number"
              inputMode="numeric"
              min={LIMITS.participants.min}
              max={LIMITS.participants.max}
              value={form.maxParticipants}
              onChange={(e) => set('maxParticipants', e.target.value)}
              placeholder="10"
              aria-invalid={Boolean(errors.maxParticipants)}
              className="sm:max-w-40"
            />
          </Field>
        </Card>

        <Card className="p-4 sm:p-6">
          <button
            type="button"
            onClick={() => setShowAts((open) => !open)}
            aria-expanded={showAts}
            className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
          >
            <span>
              <span className="block text-sm font-medium">Link to your ATS</span>
              <span className="text-muted-foreground block text-xs">
                Optional. Carried on every webhook back to Hiredude.
              </span>
            </span>
            <ChevronDown
              aria-hidden
              className={cn('size-4 shrink-0 transition-transform', showAts && 'rotate-180')}
            />
          </button>

          {showAts && (
            <div className="mt-5 space-y-5">
              <Field
                id="interviewId"
                label="Interview ID"
                optional
                error={errors.interviewId}
                hint="Becomes the room name, so letters, digits, - and _ only."
              >
                <Input
                  id="interviewId"
                  value={form.interviewId}
                  onChange={(e) => set('interviewId', e.target.value)}
                  placeholder="int_8fa21c"
                  maxLength={LIMITS.interviewId}
                  aria-invalid={Boolean(errors.interviewId)}
                  className="font-mono"
                />
              </Field>

              <Field
                id="companyRequestId"
                label="Company request ID"
                optional
                error={errors.companyRequestId}
              >
                <Input
                  id="companyRequestId"
                  value={form.companyRequestId}
                  onChange={(e) => set('companyRequestId', e.target.value)}
                  maxLength={LIMITS.externalRef}
                  aria-invalid={Boolean(errors.companyRequestId)}
                  className="font-mono"
                />
              </Field>

              <Field
                id="sortedCandidateId"
                label="Candidate ID"
                optional
                error={errors.sortedCandidateId}
              >
                <Input
                  id="sortedCandidateId"
                  value={form.sortedCandidateId}
                  onChange={(e) => set('sortedCandidateId', e.target.value)}
                  maxLength={LIMITS.externalRef}
                  aria-invalid={Boolean(errors.sortedCandidateId)}
                  className="font-mono"
                />
              </Field>
            </div>
          )}
        </Card>

        {submitError && (
          <Card
            role="alert"
            className="flex items-start gap-3 border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] p-4"
          >
            <TriangleAlert
              className="mt-0.5 size-5 shrink-0 text-[color-mix(in_oklab,var(--destructive)_80%,white)]"
              aria-hidden
            />
            <p className="text-sm text-pretty">{submitError}</p>
          </Card>
        )}

        {Object.keys(errors).length > 0 && (
          <p role="alert" className="text-muted-foreground text-sm">
            Some fields need attention. They are marked above.
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" asChild>
            <Link to="/admin/meetings">Cancel</Link>
          </Button>
          <Button type="submit" disabled={pending} className="sm:min-w-44">
            {pending ? <Spinner className="size-4" /> : <CalendarPlus className="size-4" />}
            {pending ? 'Creating…' : 'Create meeting'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function CreatedView({
  meeting,
  onAnother,
}: {
  meeting: AdminCreateMeetingResponse;
  onAnother: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6 lg:p-8">
      <BackLink />

      <Card className="mt-4 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-[var(--success)]" aria-hidden />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-balance">
              {meeting.title} is scheduled
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Meeting code{' '}
              <code className="border-border/70 rounded-md border bg-white/[0.03] px-1.5 py-0.5 font-mono text-xs select-all">
                {meeting.code}
              </code>
            </p>
          </div>
        </div>

        <Separator className="my-6" />

        <h2 className="text-sm font-medium">Join links</h2>
        <p className="text-muted-foreground mt-1 text-xs text-pretty">
          Send each person only their own link. Anyone holding a link can join as that person, so
          treat them like passwords.
        </p>
        <JoinWindowSummary
          input={{ status: meeting.status, joinOpensAt: meeting.joinOpensAt, joinClosesAt: meeting.joinClosesAt }}
          className="mt-3"
        />

        <div className="mt-4 space-y-3">
          <JoinLinkRow
            label="Interviewer"
            description={HOST_LINK_DESCRIPTION}
            url={meeting.hostUrl}
            openLabel="Open the interviewer link in a new tab"
          />
          <JoinLinkRow
            label="Candidate"
            description={GUEST_LINK_DESCRIPTION}
            url={meeting.guestUrl}
            openLabel="Open the candidate link in a new tab"
          />
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onAnother}>
            <CalendarPlus className="size-4" />
            Create another
          </Button>
          <Button asChild>
            <Link to={`/admin/meetings/${meeting.meetingId}`}>View meeting</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/meetings"
      className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-1.5 text-sm transition-colors"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Meetings
    </Link>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  optional,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <Label htmlFor={id} className="flex items-baseline gap-2">
        {label}
        {optional && <span className="text-muted-foreground text-xs font-normal">Optional</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {error ? (
        <p
          role="alert"
          className="mt-1.5 text-xs text-pretty text-[color-mix(in_oklab,var(--destructive)_80%,white)]"
        >
          {error}
        </p>
      ) : (
        hint && <p className="text-muted-foreground mt-1.5 text-xs text-pretty">{hint}</p>
      )}
    </div>
  );
}

function PartyCard({
  role,
  heading,
  caption,
  fields,
  errors,
  onChange,
}: {
  role: 'host' | 'guest';
  heading: string;
  caption: string;
  fields: PartyFields;
  errors: Errors;
  onChange: (role: 'host' | 'guest', field: keyof PartyFields, value: string) => void;
}) {
  return (
    <Card className="space-y-5 p-4 sm:p-6">
      <div>
        <h2 className="text-sm font-medium">{heading}</h2>
        <p className="text-muted-foreground mt-1 text-xs text-pretty">{caption}</p>
      </div>

      <Field id={`${role}-name`} label="Name" error={errors[`${role}.name`]}>
        <Input
          id={`${role}-name`}
          value={fields.name}
          onChange={(e) => onChange(role, 'name', e.target.value)}
          maxLength={LIMITS.name}
          autoComplete="off"
          aria-invalid={Boolean(errors[`${role}.name`])}
        />
      </Field>

      <Field id={`${role}-email`} label="Email" optional error={errors[`${role}.email`]}>
        <Input
          id={`${role}-email`}
          type="email"
          value={fields.email}
          onChange={(e) => onChange(role, 'email', e.target.value)}
          autoComplete="off"
          aria-invalid={Boolean(errors[`${role}.email`])}
        />
      </Field>

      <Field id={`${role}-phone`} label="Phone" optional error={errors[`${role}.phone`]}>
        <Input
          id={`${role}-phone`}
          type="tel"
          value={fields.phone}
          onChange={(e) => onChange(role, 'phone', e.target.value)}
          autoComplete="off"
          aria-invalid={Boolean(errors[`${role}.phone`])}
        />
      </Field>
    </Card>
  );
}
